import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import DeleteInterview from '@Application/Beneficiaries/Interviews/Commands/DeleteInterview';
import deleteInterview from '@Application/Beneficiaries/Interviews/deleteInterview';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import interviewDeletionTexts from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/interviewDeletionTexts';
import {
	endInterviewDeletion,
	startInterviewDeletion,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Signals/interviewDeletionInProgressSignal';
import interviewToDeleteSignal, {
	closeInterviewToDelete,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Signals/interviewToDeleteSignal';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

const CONFIRM_LABELS: Readonly<Record<InterviewKind, string>> = Object.freeze({
	[InterviewKind.CONVERSATION]: "Saisissez la date de l'entretien pour confirmer.",
	[InterviewKind.NOTES]: 'Saisissez la date de la prise de notes pour confirmer.',
	[InterviewKind.TEXT]: 'Saisissez la date du texte saisi pour confirmer.',
});

/**
 * Hard-deletion confirmation, opened by `interviewToDeleteSignal` (the id only). The interview is read
 * from the dossier's list, the one the detail screen itself resolves from. The date to retype is the
 * interview's own, `JJ/MM/AAAA`.
 */
const DeleteInterviewDialog: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const { subject } = useSubjectAddressParams();

	const interviewToDeleteId = useSignalValue(interviewToDeleteSignal);
	const interview = useSelector((state) =>
		interviewToDeleteId === null
			? null
			: (state.interviewsStore.interviews?.findById(interviewToDeleteId) ?? null),
	);

	const kind = interview?.knownKind ?? InterviewKind.CONVERSATION;
	const texts = interviewDeletionTexts[kind];
	const message =
		interview?.recording?.status.value === 'uploading'
			? `${texts.baseMessage} ${texts.uploadingMessage}`
			: texts.baseMessage;

	const handleHide = React.useCallback(() => {
		closeInterviewToDelete();
	}, []);

	const handleAccept = React.useCallback(() => {
		if (interview === null || subject === null) {
			return;
		}

		// `acceptCallback` never closes the dialog itself: close it at once. The in-progress signal keeps
		// the detail screen on its skeleton while the success reducer empties its data. On success it is
		// not reset here (a reset would run before React commits the navigation and flash "not found"):
		// the detail screen resets it on unmount. On refusal it is reset at once, the user stays on the
		// page and the failure toast shows the server's message.
		closeInterviewToDelete();
		startInterviewDeletion(interview.id);
		dispatch(deleteInterview(new DeleteInterview(currentFacility.id, subject, interview.id)))
			.then(() => {
				navigate(buildSubjectPath(subject));
			})
			.catch(() => {
				endInterviewDeletion();
				ignoreHandledError();
			});
	}, [interview, dispatch, currentFacility.id, subject, navigate]);

	return (
		<ConfirmDeleteDialog
			visible={interview !== null}
			onHide={handleHide}
			header={texts.header}
			message={message}
			confirmText={interview?.conductedOn?.toDisplay() ?? ''}
			confirmLabel={CONFIRM_LABELS[kind]}
			acceptCallback={handleAccept}
		/>
	);
};

export default DeleteInterviewDialog;
